import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import ModuleCategoryPicker from "./ModuleCategoryPicker";
import ScheduledAutomationTab from "./ScheduledAutomationTab";
import ScheduledAutomationCards from "./ScheduledAutomationCards";
import {
  EVENT_CONFIG,
  CRON_JOB_CONFIG,
  flattenCronJobsByCategory,
} from "./automationConfigs";
import {
  fetchNotificationTypes,
  fetchScheduledEventsByCategory,
  fetchScheduledEvents,
  fetchScheduledCronJobs,
  fetchAutomationsWithScheduledCronJobsByCategory,
} from "./scheduledAutomationsApi";
import { getLaunchId } from "./automationConfigs";
import "./ScheduledAutomationsPage.scss";

const TABS = [
  { key: EVENT_CONFIG.key, label: "Notification", icon: "mdi:bell-outline" },
  {
    key: CRON_JOB_CONFIG.key,
    label: "Automation",
    icon: "mdi:timer-cog-outline",
  },
];

const ScheduledDetails = ({
  items,
  onEdit,
  onTrigger,
  selectable,
  selectedIds,
  onToggleSelect,
  selectedAutomationId,
  onSelectAutomation,
  typeId,
}) => (
  <div className="event-type-card__details">
    {items.map((item) => {
      const template = item.eventTemplate || item.cronJobTemplate || {};
      const notificationTypes = item.notificationTypes || [];
      const selectionId =
        item.notification?.id ?? item.automation?.automationId;
      return (
        <article
          className={`event-type-card__detail ${
            selectedAutomationId ===
            (item.scheduledEventId || item.scheduledCronJobId)
              ? "event-type-card__detail--active"
              : ""
          }`}
          key={item.scheduledEventId || item.scheduledCronJobId}
          onClick={() => onSelectAutomation?.({ ...item, typeId })}
        >
          <div className="event-type-card__detail-header">
            <strong>{item.taskName || "Unnamed configuration"}</strong>
            <div className="event-type-card__detail-actions">
              {selectable && (
                <input
                  type="checkbox"
                  checked={selectedIds.has(selectionId)}
                  onChange={() => onToggleSelect(selectionId)}
                  disabled={selectionId == null}
                  aria-label={`Select ${item.taskName || "notification"}`}
                />
              )}
              <span className="status-badge status-badge--positive">
                {item.status || "Enabled"}
              </span>
              {onEdit && (
                <button
                  type="button"
                  className="btn btn--secondary event-type-card__edit"
                  onClick={() => onEdit(item)}
                >
                  <Icon icon="mdi:pencil-outline" width="15" />
                  Edit
                </button>
              )}
              {onTrigger && (
                <button
                  type="button"
                  className="btn btn--secondary event-type-card__trigger"
                  onClick={() => onTrigger(item)}
                >
                  {/LAUNCHED/i.test(
                    item?.notification?.status || item?.automation?.status || "",
                  )
                    ? "Put on hold"
                    : "Launch"}
                </button>
              )}
            </div>
          </div>
          <div className="event-type-card__detail-grid">
            <span><b>Notifications:</b> {notificationTypes.map((type) => type.notificationType).join(", ") || "—"}</span>
            <span><b>Template:</b> {template.templateName || "—"}</span>
            <span><b>Subject:</b> {template.subject || "—"}</span>
            <span><b>Send to:</b> {item.toEmailSourceFrom || "—"}</span>
            <span><b>Send from:</b> {item.fromEmailSource || "—"}</span>
          </div>
          {(template.notificationHeading || template.notificationMessage) && (
            <div className="event-type-card__in-app">
              {template.notificationHeading && <b>{template.notificationHeading}</b>}
              {template.notificationMessage && <span>{template.notificationMessage}</span>}
            </div>
          )}
        </article>
      );
    })}
  </div>
);

const TypeCards = ({
  types,
  label,
  onAdd,
  scheduledByType = {},
  loadingDetails,
  onEdit,
  onTrigger,
  selectable,
  selectedIds,
  onToggleSelect,
  selectedAutomationId,
  onSelectAutomation,
}) => (
  <div className="event-type-list">
    {types.length ? (
      types.map((type) => (
        <div
          className={`event-type-card ${
            selectedAutomationId?.typeId === type.id
              ? "event-type-card--active"
              : ""
          }`}
          key={type.id}
        >
          <button
            type="button"
            className="event-type-card__heading"
            onClick={() => onSelectAutomation?.({ typeId: type.id })}
          >
            <strong>{type.name}</strong>
            <span>Configure a {label.toLowerCase()} for this type</span>
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onAdd(type.id)}
          >
            <Icon icon="mdi:plus" width="18" /> Add
          </button>
          {loadingDetails ? (
            <small className="event-type-card__loading">Loading details…</small>
          ) : scheduledByType[type.id]?.length ? (
            <ScheduledDetails
              items={scheduledByType[type.id]}
              onEdit={onEdit}
              onTrigger={onTrigger}
              selectable={selectable}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              selectedAutomationId={selectedAutomationId?.itemId}
              onSelectAutomation={(item) =>
                onSelectAutomation?.({ ...item, typeId: type.id })
              }
              typeId={type.id}
            />
          ) : (
            <small className="event-type-card__empty">No scheduled configuration yet.</small>
          )}
        </div>
      ))
    ) : (
      <div className="automation-table__state">
        No {label.toLowerCase()} types found for this category.
      </div>
    )}
  </div>
);

const STATUS_OPTIONS = ["LAUNCHED", "ON_HOLD"];

/**
 * Notification tab: Module -> Category badges, then manage scheduled
 * events. GET /scheduled-events/category/{categoryId} only returns event
 * *types* for the category, so selecting a category scopes the "Event
 * type" dropdown in the New/Edit form — the table below still lists ALL
 * scheduled events via EVENT_CONFIG's unfiltered list() (there's no
 * confirmed "list events by category" instance endpoint yet, unlike the
 * cron job side below).
 */
const NotificationTab = ({ notificationTypeOptions }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [newTypeId, setNewTypeId] = useState(null);
  const [scheduledByType, setScheduledByType] = useState({});
  const [externalEditItem, setExternalEditItem] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState(
    new Set(),
  );
  const [bulkLaunching, setBulkLaunching] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("LAUNCHED");
  const notificationIds = Object.values(scheduledByType)
    .flat()
    .map((item) => item.notification?.id)
    .filter((id) => id != null);
  const allNotificationsSelected =
    notificationIds.length > 0 &&
    notificationIds.every((id) => selectedNotificationIds.has(id));

  const toggleNotification = (id) =>
    setSelectedNotificationIds((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAllNotifications = (checked) => {
    setSelectedNotificationIds(checked ? new Set(notificationIds) : new Set());
  };

  const refreshNotifications = async () => {
    const response = await fetchScheduledEvents();
    const scheduledEvents = response.data?.data || [];
    setScheduledByType(
      typeOptions.reduce((result, type) => {
        result[type.eventId] = scheduledEvents.filter(
          (event) => Number(event.eventTypeId) === Number(type.eventId),
        );
        return result;
      }, {}),
    );
  };

  const handleSingleNotificationLaunch = async (item) => {
    const notificationId = item?.notification?.id;
    if (notificationId == null) {
      toast.error("This notification has no notification ID to launch.");
      return;
    }
    try {
      const isLaunched = /LAUNCHED/i.test(item.notification?.status || "");
      await EVENT_CONFIG.api.launch(
        [notificationId],
        isLaunched ? "ON_HOLD" : "LAUNCHED",
      );
      toast.success(
        isLaunched
          ? "Notification put on hold."
          : "Notification launched.",
      );
      await refreshNotifications();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Couldn't update the notification status.",
      );
    }
  };

  const launchSelectedNotifications = async () => {
    if (!selectedNotificationIds.size) return;
    setBulkLaunching(true);
    try {
      await EVENT_CONFIG.api.launch(
        Array.from(selectedNotificationIds),
        bulkStatus,
      );
      toast.success(
        `${selectedNotificationIds.size} notifications ${
          bulkStatus === "ON_HOLD" ? "put on hold" : "launched"
        }.`,
      );
      setSelectedNotificationIds(new Set());
      const response = await fetchScheduledEvents();
      const scheduledEvents = response.data?.data || [];
      setScheduledByType(
        typeOptions.reduce((result, type) => {
          result[type.eventId] = scheduledEvents.filter(
            (event) => Number(event.eventTypeId) === Number(type.eventId),
          );
          return result;
        }, {}),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Couldn't launch the selected notifications.",
      );
    } finally {
      setBulkLaunching(false);
    }
  };

  const handleCategorySelect = useCallback(async (category) => {
    setSelectedCategory(category);
    setNewTypeId(null);
    setTypeOptions([]);
    setScheduledByType({});
    setLoadingTypes(true);
    try {
      const [typesResponse, notificationsResponse] = await Promise.all([
        fetchScheduledEventsByCategory(category.categoryId),
        fetchScheduledEvents(),
      ]);
      // Map {eventTypeId, eventTypeName} -> {eventId, eventName} to match
      // EVENT_CONFIG.typeOption.
      const mapped = (typesResponse.data?.data || []).map((t) => ({
        eventId: t.eventTypeId,
        eventName: t.eventTypeName,
      }));
      setTypeOptions(mapped);
      const firstTypeId = mapped[0]?.eventId ?? null;
      setSelectedTypeId(firstTypeId);
      setSelectedAutomationId(null);
      const scheduledEvents = notificationsResponse.data?.data || [];
      setScheduledByType(
        mapped.reduce((result, type) => {
          result[type.eventId] = scheduledEvents.filter(
            (item) => Number(item.eventTypeId) === Number(type.eventId),
          );
          setSelectedAutomationId(
            scheduledEvents.find(
              (item) => Number(item.eventTypeId) === Number(firstTypeId),
            )?.scheduledEventId ?? null,
          );
          return result;
        }, {}),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Couldn't load event types or scheduled notification details.",
      );
    } finally {
      setLoadingTypes(false);
    }
  }, []);

  return (
    <>
      <ModuleCategoryPicker onCategorySelect={handleCategorySelect} />

      {selectedCategory && (
        <>
          <h3 className="category-scope-heading">
            Scheduled Events — {selectedCategory.categoryName}
          </h3>
          {loadingTypes ? (
            <div className="automation-table__state">Loading event types…</div>
          ) : (
            <>
              <div className="event-type-card__bulk-actions">
                <label className="event-type-card__select-all">
                  <input
                    type="checkbox"
                    checked={allNotificationsSelected}
                    onChange={(event) =>
                      toggleAllNotifications(event.target.checked)
                    }
                    disabled={!notificationIds.length}
                  />
                  Select all
                </label>
                <select
                  className="launch-status-select"
                  value={bulkStatus}
                  onChange={(event) => setBulkStatus(event.target.value)}
                  aria-label="Notification bulk status"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status === "ON_HOLD" ? "Put on hold" : "Launch"}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!selectedNotificationIds.size || bulkLaunching}
                  onClick={launchSelectedNotifications}
                >
                  <Icon icon="mdi:rocket-launch-outline" width="18" />
                  {bulkLaunching
                    ? "Launching…"
                    : `${
                        bulkStatus === "ON_HOLD"
                          ? "Put on hold selected"
                          : "Launch selected"
                      }${
                        selectedNotificationIds.size
                          ? ` (${selectedNotificationIds.size})`
                          : ""
                      }`}
                </button>
              </div>
              <TypeCards
                types={typeOptions.map((type) => ({
                  id: type.eventId,
                  name: type.eventName,
                }))}
                label="Notification"
                onAdd={(id) => setNewTypeId({ id, key: Date.now() })}
                scheduledByType={scheduledByType}
                selectedAutomationId={{
                  typeId: selectedTypeId,
                  itemId: selectedAutomationId,
                }}
                onSelectAutomation={(item) => {
                  if (item.typeId == null) return;
                  setSelectedTypeId(item.typeId);
                  setSelectedAutomationId(
                    item.scheduledEventId ??
                      scheduledByType[item.typeId]?.[0]?.scheduledEventId ??
                      null,
                  );
                }}
                onEdit={(item) => setExternalEditItem(item)}
                onTrigger={handleSingleNotificationLaunch}
                selectable
                selectedIds={selectedNotificationIds}
                onToggleSelect={toggleNotification}
              />
              <ScheduledAutomationTab
                key={selectedCategory.categoryId}
                config={EVENT_CONFIG}
                notificationTypeOptions={notificationTypeOptions}
                typeOptions={typeOptions}
                ItemsView={ScheduledAutomationCards}
                newTypeId={newTypeId}
                showToolbar={false}
                showItems={false}
                externalEditItem={externalEditItem}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

/**
 * Automation tab: Module -> Category badges, then manage scheduled cron
 * jobs. GET /scheduled-cron-jobs/notifications/cron-jobs/{categoryId}
 * returns the category's cron job types *and* their existing scheduled cron
 * job records nested underneath, so one call drives both the "Cron job
 * type" dropdown and a table that's genuinely filtered to the category.
 *
 * A category-scoped clone of CRON_JOB_CONFIG is built here whose api.list()
 * re-fetches and re-flattens that endpoint every time it's called
 * (including the tab's refresh button and the reload right after a
 * create/update), so the type options and table rows both stay in sync with
 * the selected category.
 */
const AutomationTab = ({ notificationTypeOptions }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [newTypeId, setNewTypeId] = useState(null);
  const [scheduledByType, setScheduledByType] = useState({});
  const [externalEditItem, setExternalEditItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);
  const [selectedAutomationIds, setSelectedAutomationIds] = useState(
    new Set(),
  );
  const [bulkStatus, setBulkStatus] = useState("LAUNCHED");
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    setTypeOptions([]);
    setNewTypeId(null);
    setSelectedTypeId(null);
    setSelectedAutomationId(null);
    setSelectedAutomationIds(new Set());
  }, []);

  const categoryConfig = useMemo(() => {
    if (!selectedCategory) return null;
    return {
      ...CRON_JOB_CONFIG,
      refreshKey,
      api: {
        ...CRON_JOB_CONFIG.api,
        list: async () => {
          const [categoryResponse, automationsResponse] = await Promise.all([
            fetchAutomationsWithScheduledCronJobsByCategory(
              selectedCategory.categoryId,
            ),
            fetchScheduledCronJobs(),
          ]);
          const { typeOptions: opts } = flattenCronJobsByCategory(
            categoryResponse.data?.data || [],
            selectedCategory.categoryId,
          );
          const typeIds = new Set(opts.map((type) => type.cronJobId));
          const items = (automationsResponse.data?.data || []).filter((item) =>
            typeIds.has(Number(item.cronJobTypeId)),
          );
          setTypeOptions(opts);
          setScheduledByType(
            opts.reduce((result, type) => {
              result[type.cronJobId] = items.filter(
                (item) =>
                  Number(item.cronJobTypeId) === Number(type.cronJobId),
              );
              const firstType = opts[0];
              const firstAutomation = firstType
                ? items.find(
                    (item) =>
                      Number(item.cronJobTypeId) === Number(firstType.cronJobId),
                  )
                : null;
              setSelectedTypeId((current) =>
                opts.some((type) => Number(type.cronJobId) === Number(current))
                  ? current
                  : (firstType?.cronJobId ?? null),
              );
              setSelectedAutomationId((current) => {
                const currentItem = items.find(
                  (item) => Number(item.scheduledCronJobId) === Number(current),
                );
                return currentItem &&
                  Number(currentItem.cronJobTypeId) === Number(firstType?.cronJobId)
                  ? current
                  : (firstAutomation?.scheduledCronJobId ?? null);
              });
              return result;
            }, {}),
          );
          return { data: { data: items } };
        },
      },
    };
  }, [selectedCategory, refreshKey]);

  const handleTrigger = async (item) => {
    const automationId = getLaunchId(CRON_JOB_CONFIG, item);
    if (automationId == null) return;
    try {
      const isLaunched = /LAUNCHED/i.test(item?.automation?.status || "");
      await CRON_JOB_CONFIG.api.launch(
        [automationId],
        isLaunched ? "ON_HOLD" : "LAUNCHED",
      );
      toast.success(isLaunched ? "Automation put on hold." : "Automation launched.");
      setRefreshKey((value) => value + 1);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Couldn't trigger the automation.",
      );
    }
  };

  const automationItems = Object.values(scheduledByType).flat();
  const automationIds = automationItems
    .map((item) => getLaunchId(CRON_JOB_CONFIG, item))
    .filter((id) => id != null);
  const allAutomationsSelected =
    automationIds.length > 0 &&
    automationIds.every((id) => selectedAutomationIds.has(id));

  const toggleAutomation = (id) =>
    setSelectedAutomationIds((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAllAutomations = (checked) => {
    setSelectedAutomationIds(checked ? new Set(automationIds) : new Set());
  };

  const updateSelectedAutomations = async () => {
    if (!selectedAutomationIds.size) return;
    setBulkUpdating(true);
    try {
      await CRON_JOB_CONFIG.api.launch(
        Array.from(selectedAutomationIds),
        bulkStatus,
      );
      toast.success(
        `${selectedAutomationIds.size} automations ${
          bulkStatus === "ON_HOLD" ? "put on hold" : "launched"
        }.`,
      );
      setSelectedAutomationIds(new Set());
      setRefreshKey((value) => value + 1);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Couldn't update the selected automations.",
      );
    } finally {
      setBulkUpdating(false);
    }
  };

  return (
    <>
      <ModuleCategoryPicker onCategorySelect={handleCategorySelect} />

      {selectedCategory && categoryConfig && (
        <>
          <h3 className="category-scope-heading">
            Scheduled Cron Jobs — {selectedCategory.categoryName}
          </h3>
          <ScheduledAutomationTab
            key={selectedCategory.categoryId}
            config={categoryConfig}
            notificationTypeOptions={notificationTypeOptions}
            typeOptions={typeOptions}
            ItemsView={ScheduledAutomationCards}
            newTypeId={newTypeId}
            showNew={false}
            showToolbar={false}
            showItems={false}
            externalEditItem={externalEditItem}
          />
          <div className="event-type-card__bulk-actions">
            <label className="event-type-card__select-all">
              <input
                type="checkbox"
                checked={allAutomationsSelected}
                onChange={(event) =>
                  toggleAllAutomations(event.target.checked)
                }
                disabled={!automationIds.length}
              />
              Select all
            </label>
            <select
              className="launch-status-select"
              value={bulkStatus}
              onChange={(event) => setBulkStatus(event.target.value)}
              aria-label="Automation bulk status"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === "ON_HOLD" ? "Put on hold" : "Launch"}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!selectedAutomationIds.size || bulkUpdating}
              onClick={updateSelectedAutomations}
            >
              <Icon icon="mdi:rocket-launch-outline" width="18" />
              {bulkUpdating
                ? "Updating…"
                : `${
                    bulkStatus === "ON_HOLD"
                      ? "Put on hold selected"
                      : "Launch selected"
                  }${
                    selectedAutomationIds.size
                      ? ` (${selectedAutomationIds.size})`
                      : ""
                  }`}
            </button>
          </div>
          <TypeCards
            types={typeOptions.map((type) => ({
              id: type.cronJobId,
              name: type.eventName,
            }))}
            label="Automation"
            onAdd={(id) => setNewTypeId({ id, key: Date.now() })}
            scheduledByType={scheduledByType}
            selectedAutomationId={{
              typeId: selectedTypeId,
              itemId: selectedAutomationId,
            }}
            onSelectAutomation={(item) => {
              if (item.typeId == null) return;
              setSelectedTypeId(item.typeId);
              setSelectedAutomationId(
                item.scheduledCronJobId ??
                  scheduledByType[item.typeId]?.[0]?.scheduledCronJobId ??
                  null,
              );
            }}
            onEdit={(item) => setExternalEditItem(item)}
            onTrigger={handleTrigger}
            selectable
            selectedIds={selectedAutomationIds}
            onToggleSelect={toggleAutomation}
          />
        </>
      )}
    </>
  );
};

const ScheduledAutomationsPage = () => {
  const [activeKey, setActiveKey] = useState(EVENT_CONFIG.key);
  const [notificationTypeOptions, setNotificationTypeOptions] = useState([]);

  // Notification types (rarely change) are fetched once here and shared by
  // whichever tab is active, instead of re-fetching per tab switch.
  useEffect(() => {
    fetchNotificationTypes()
      .then((res) => setNotificationTypeOptions(res.data?.data || []))
      .catch(() => toast.error("Couldn't load notification types."));
  }, []);

  return (
    <div className="scheduled-automations-page">
      <header className="scheduled-automations-page__header">
        <h1>Notification & Automation</h1>
        <p>
          Pick a module and category, then manage scheduled events and cron jobs
          for it.
        </p>
      </header>

      <div className="scheduled-automations-page__tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            type="button"
            aria-selected={activeKey === tab.key}
            className={`tab-button ${
              activeKey === tab.key ? "tab-button--active" : ""
            }`}
            onClick={() => setActiveKey(tab.key)}
          >
            <Icon icon={tab.icon} width="18" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeKey === EVENT_CONFIG.key ? (
        <NotificationTab notificationTypeOptions={notificationTypeOptions} />
      ) : (
        <AutomationTab notificationTypeOptions={notificationTypeOptions} />
      )}
    </div>
  );
};

export default ScheduledAutomationsPage;
